/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2,
} from '@angular/core';

import {Focusable} from '../core/a11y/focus-key-manager';
import {coerceBooleanProperty} from '../core/coercion/boolean-property';
import {CanColor, mixinColor} from '../core/common-behaviors/color';
import {CanDisable, mixinDisabled} from '../core/common-behaviors/disabled';

export interface MdChipEvent {
  chip: MdChip;
}
export class MdChipBase {
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(public _renderer: Renderer2,
public _elementRef: ElementRef) {}
}

function MdChipBase_tsickle_Closure_declarations() {
/** @type {?} */
MdChipBase.prototype._renderer;
/** @type {?} */
MdChipBase.prototype._elementRef;
}

export const /** @type {?} */ _MdChipMixinBase = mixinColor(mixinDisabled(MdChipBase), 'primary');
/**
 * Dummy directive to add CSS class to basic chips.
 * \@docs-private
 */
export class MdBasicChip { static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: `md-basic-chip, [md-basic-chip], mat-basic-chip, [mat-basic-chip]`,
  host: {'class': 'mat-basic-chip'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdBasicChip_tsickle_Closure_declarations() {
/** @type {?} */
MdBasicChip.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdBasicChip.ctorParameters;
}

/**
 * Material design styled Chip component. Used inside the MdChipList component.
 */
export class MdChip extends _MdChipMixinBase implements Focusable, OnDestroy, CanColor, CanDisable {
/**
 * Whether the chip is selected.
 * @return {?}
 */
get selected(): boolean { return this._selected; }
/**
 * @param {?} value
 * @return {?}
 */
set selected(value: boolean) {
    this._selected = coerceBooleanProperty(value);
    (this.selected ? this.select : this.deselect).emit({chip: this});
  }
  protected _selected: boolean = false;
/**
 * Whether the chip has focus.
 */
_hasFocus: boolean = false;
/**
 * Emitted when the chip is focused.
 */
onFocus = new EventEmitter<MdChipEvent>();
/**
 * Emitted when the chip is selected.
 */
select = new EventEmitter<MdChipEvent>();
/**
 * Emitted when the chip is deselected.
 */
deselect = new EventEmitter<MdChipEvent>();
/**
 * Emitted when the chip is destroyed.
 */
destroy = new EventEmitter<MdChipEvent>();
/**
 * @param {?} renderer
 * @param {?} elementRef
 */
constructor(renderer: Renderer2, elementRef: ElementRef) {
    super(renderer, elementRef);
  }
/**
 * @return {?}
 */
ngOnDestroy(): void {
    this.destroy.emit({chip: this});
  }
/**
 * Toggles the current selected state of this chip.
 * @return {?} Whether the chip is selected.
 */
toggleSelected(): boolean {
    this.selected = !this.selected;
    return this.selected;
  }
/**
 * Allows for programmatic focusing of the chip.
 * @return {?}
 */
focus(): void {
    this._elementRef.nativeElement.focus();
    this.onFocus.emit({chip: this});
  }
/**
 * The aria-disabled state for the chip
 * @return {?}
 */
_isAriaDisabled(): string {
    return String(this.disabled);
  }
/**
 * Ensures events fire properly upon click.
 * @param {?} event
 * @return {?}
 */
_handleClick(event: Event) {
    // Check disabled
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      this.focus();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: `md-basic-chip, [md-basic-chip], md-chip, [md-chip],
             mat-basic-chip, [mat-basic-chip], mat-chip, [mat-chip]`,
  inputs: ['color', 'disabled'],
  host: {
    'class': 'mat-chip',
    'tabindex': '-1',
    'role': 'option',
    '[class.mat-chip-selected]': 'selected',
    '[attr.disabled]': 'disabled || null',
    '[attr.aria-disabled]': '_isAriaDisabled()',
    '(click)': '_handleClick($event)',
    '(focus)': '_hasFocus = true',
    '(blur)': '_hasFocus = false',
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'selected': [{ type: Input },],
'select': [{ type: Output },],
'deselect': [{ type: Output },],
'destroy': [{ type: Output },],
};
}

function MdChip_tsickle_Closure_declarations() {
/** @type {?} */
MdChip.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdChip.ctorParameters;
/** @type {?} */
MdChip.propDecorators;
/** @type {?} */
MdChip.prototype._selected;
/**
 * Whether the chip has focus.
 * @type {?}
 */
MdChip.prototype._hasFocus;
/**
 * Emitted when the chip is focused.
 * @type {?}
 */
MdChip.prototype.onFocus;
/**
 * Emitted when the chip is selected.
 * @type {?}
 */
MdChip.prototype.select;
/**
 * Emitted when the chip is deselected.
 * @type {?}
 */
MdChip.prototype.deselect;
/**
 * Emitted when the chip is destroyed.
 * @type {?}
 */
MdChip.prototype.destroy;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
